import React, { useEffect, useState } from 'react';
import { Stock } from '../model/stock';
import { StockService } from '../../services/stock.service';
import { Store, StoreResponse } from '../../interfaces/store.interface';
import { StockLot } from '../../interfaces/stock.interface';
import { getStoresByInstitutionId } from '../../services/store.service';
import {
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  Pagination,
  SelectChangeEvent,
  IconButton,
  Collapse,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { UserType } from '../../interfaces/userType.enum';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

// StockController.setReorderLevel's @PreAuthorize: StoreAdmin, StoreManager, InstitutionAdmin.
// StoreStaff can read this list but would get a 403 from the PATCH, so they are not offered it.
const REORDER_EDIT_ROLES = [UserType.InstitutionAdmin, UserType.StoreAdmin, UserType.StoreManager];

interface StockRowProps {
  stock: Stock;
  rowNumber: number;
  canEditReorder: boolean;
  onEditReorder: (stock: Stock) => void;
}

const StockRow: React.FC<StockRowProps> = ({ stock, rowNumber, canEditReorder, onEditReorder }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [lots, setLots] = useState<StockLot[]>([]);
  const [lotsLoading, setLotsLoading] = useState<boolean>(false);
  const [lotsLoaded, setLotsLoaded] = useState<boolean>(false);

  const isLotTracked = stock.item?.lotTracked === true;

  const handleToggle = async () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen && !lotsLoaded && stock.item?.id) {
      setLotsLoading(true);
      try {
        const data = await StockService.getLots(stock.storeId, stock.item.id);
        setLots(data);
        setLotsLoaded(true);
      } catch (error) {
        console.error('Error fetching lots:', error);
      } finally {
        setLotsLoading(false);
      }
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          {isLotTracked && (
            <IconButton size="small" onClick={handleToggle} aria-label="expand row">
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{rowNumber}</TableCell>
        <TableCell>{stock.item.itemName}</TableCell>
        <TableCell>{stock.item?.itemCategory?.itemCategoryName}</TableCell>
        <TableCell>{stock.store.storeName}</TableCell>
        <TableCell>{stock.availableQty}</TableCell>
        <TableCell>{stock.item?.unit?.symbol ?? '-'}</TableCell>
        <TableCell>{stock.totalValue ?? '-'}</TableCell>
        <TableCell>
          {stock.reorderLevel ?? '-'}
          {canEditReorder && (
            <IconButton
              size="small"
              sx={{ ml: 1 }}
              aria-label={`Edit reorder level for ${stock.item.itemName}`}
              onClick={() => onEditReorder(stock)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
        </TableCell>
      </TableRow>
      {isLotTracked && (
        <TableRow>
          <TableCell colSpan={9} sx={{ py: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ m: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Lots
                </Typography>
                {lotsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell>Lot No</TableCell>
                        <TableCell>Receipt Date</TableCell>
                        <TableCell>Unit Cost</TableCell>
                        <TableCell>Remaining Qty</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lots.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            No lots found
                          </TableCell>
                        </TableRow>
                      ) : (
                        lots.map(lot => (
                          <TableRow key={lot.id}>
                            <TableCell>{lot.lotNo}</TableCell>
                            <TableCell>{lot.receiptDate}</TableCell>
                            <TableCell>{lot.unitCost}</TableCell>
                            <TableCell>{lot.remainingQty}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const StockList: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const userRoles = user && user.roles ? (Array.isArray(user.roles) ? user.roles : [user.roles]) : [];
  const canEditReorder = REORDER_EDIT_ROLES.some(r => userRoles.includes(r));
  const [editing, setEditing] = useState<Stock | null>(null);
  const [reorderInput, setReorderInput] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [pageInfo, setPageInfo] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    size: 10
  });

  // Fetch stores on component mount
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response: StoreResponse = await getStoresByInstitutionId(0, 100); // Fetch all stores with a large size
        setStores(response.content);

        // If stores are available, select the first one by default
        if (response.content.length > 0) {
          setSelectedStoreId(response.content[0].id || '');
        }
      } catch (error) {
        console.error('Error fetching stores:', error);
      }
    };

    fetchStores();
  }, []);

  // Debounce the search term
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Reset to first page whenever the debounced search changes
  useEffect(() => {
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  }, [debouncedSearch]);

  // Fetch stocks when a store is selected, the page changes, or the search changes
  useEffect(() => {
    if (selectedStoreId) {
      fetchStocks(selectedStoreId, pageInfo.currentPage, debouncedSearch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStoreId, pageInfo.currentPage, debouncedSearch]);

  const fetchStocks = async (storeId: string, page: number, search: string) => {
    setLoading(true);
    try {
      const response = await StockService.getStockByStore(storeId, page, pageInfo.size, search);
      setStocks(response.content);
      setPageInfo(prev => ({
        ...prev,
        currentPage: response.number,
        totalPages: response.totalPages,
        totalElements: response.totalElements,
        size: response.size
      }));
    } catch (error) {
      console.error('Error fetching stocks:', error);
    } finally {
      setLoading(false);
    }
  };

  const openReorderEditor = (stock: Stock) => {
    setEditing(stock);
    setReorderInput(stock.reorderLevel != null ? String(stock.reorderLevel) : '');
  };

  // Blank clears the level; otherwise a whole number >= 0.
  const trimmedReorder = reorderInput.trim();
  const reorderInvalid = trimmedReorder !== '' && !/^\d+$/.test(trimmedReorder);

  const saveReorderLevel = async () => {
    if (!editing?.id || reorderInvalid) return;
    setSaving(true);
    try {
      const updated = await StockService.setReorderLevel(
        editing.id, trimmedReorder === '' ? null : Number(trimmedReorder));
      setStocks(prev => prev.map(s => (s.id === editing.id ? { ...s, reorderLevel: updated.reorderLevel } : s)));
      toast.showSuccess('Reorder level updated');
      setEditing(null);
    } catch (error) {
      toast.showApiError(error, 'Failed to update reorder level');
    } finally {
      setSaving(false);
    }
  };

  const handleStoreChange = (event: SelectChangeEvent) => {
    const storeId = event.target.value;
    setSelectedStoreId(storeId);
    setPageInfo(prev => ({...prev, currentPage: 0})); // Reset to first page when store changes
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setPageInfo(prev => ({...prev, currentPage: page - 1}));
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
        Stock List
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl sx={{ width: '300px' }}>
          <InputLabel id="store-select-label">Select Store</InputLabel>
          <Select
            labelId="store-select-label"
            id="store-select"
            value={selectedStoreId}
            label="Select Store"
            onChange={handleStoreChange}
            disabled={stores.length === 0}
          >
            {stores.length === 0 && (
              <MenuItem value="">
                <em>No stores available</em>
              </MenuItem>
            )}
            {stores.map(store => (
              <MenuItem key={store.id} value={store.id || ''}>
                {store.storeName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          sx={{ width: '300px' }}
          label="Search by item name or code"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell />
                  <TableCell>#</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Store</TableCell>
                  <TableCell>Available Quantity</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Reorder Level</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stocks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No stock items found
                    </TableCell>
                  </TableRow>
                ) : (
                  stocks.map((stock, index) => (
                    <StockRow
                      key={stock.id}
                      stock={stock}
                      rowNumber={index + 1 + pageInfo.currentPage * pageInfo.size}
                      canEditReorder={canEditReorder}
                      onEditReorder={openReorderEditor}
                    />
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {pageInfo.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={pageInfo.totalPages}
                page={pageInfo.currentPage + 1}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      <Dialog open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="xs">
        <DialogTitle>Reorder level{editing ? ` - ${editing.item.itemName}` : ''}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Reorder level"
            value={reorderInput}
            onChange={(e) => setReorderInput(e.target.value)}
            error={reorderInvalid}
            helperText={reorderInvalid
              ? 'Enter a whole number, 0 or more'
              : 'The item appears on Low Stock when available quantity is at or below this. Leave blank to clear.'}
            inputProps={{ inputMode: 'numeric' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveReorderLevel} disabled={saving || reorderInvalid}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default StockList;
